import React, {FormEvent, useCallback, useContext, useEffect, useRef, useState} from "react";
import {Modal} from "reactstrap";
import UserContext from "../../../context/UserContext";
import {ourToast} from "../../../ui-kit/src/atoms/Toast";
import {isEmailFormat} from "../../../ui-kit/src/stringUtils";
import styles from "./ManageMembersModal.module.scss";
import {ADMIN_ROLE, DEFAULT_ROLE, getStaticRole, STATIC_ROLES, StaticRole} from "./memberRoles";
import {
  addMember,
  cancelInvite,
  createInvite,
  fetchInvites,
  getErrorMessage,
  getErrorSubstatus,
  Invite,
  listMembers,
  listRoles,
  Member,
  MembersPagination,
  removeMember,
  setMemberRole
} from "./membersApi";

const PER_PAGE = 25;
// The pending invites are few. Load them in one page (the backend maximum).
const INVITES_PER_PAGE = 100;
const SEARCH_DELAY_MS = 300;
const HIGHLIGHT_MS = 2400;

type ManageMembersModalProps = {
  isOpen: boolean,
  toggle: () => void,
}

type LoadStatus = 'loading' | 'success' | 'error';
type AddMode = 'member' | 'invite';
type RoleName = StaticRole['name'];

function toSystemUserId(id: string | null | undefined): string | null {
  if (!id) {
    return null;
  }
  try {
    const parts = atob(id).split(":");
    if (parts.length > 1) {
      return `core:user:${parts[parts.length - 1]}`;
    }
  } catch (e) {
    // The id is not a base64 relay id
  }
  return id;
}

function getMemberName(member: Member): string {
  return `${member.first_name || ''} ${member.last_name || ''}`.trim();
}

function getInitials(member: Member): string {
  const initials = [member.first_name, member.last_name]
    .filter(part => !!part && part.trim().length > 0)
    .map(part => part.trim().charAt(0))
    .join("");
  if (initials) {
    return initials.toUpperCase();
  }
  return member.email ? member.email.charAt(0).toUpperCase() : "?";
}

function getMemberRole(member: Member): string {
  return member.roles.length > 0 ? member.roles[0] : DEFAULT_ROLE;
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString(undefined, {day: 'numeric', month: 'short', year: 'numeric'});
}

function formatLastLogin(lastLogin: string | null): string {
  if (!lastLogin) {
    return "Never signed in";
  }
  const date = new Date(lastLogin);
  if (isNaN(date.getTime())) {
    return "Never signed in";
  }
  return `Last signed in ${formatShortDate(date)}`;
}

function formatInvitedAt(createdAt: string | null): string {
  if (!createdAt) {
    return "Invite sent";
  }
  const date = new Date(createdAt);
  if (isNaN(date.getTime())) {
    return "Invite sent";
  }
  const days = Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) {
    return "Invited today";
  }
  if (days === 1) {
    return "Invited yesterday";
  }
  if (days < 30) {
    return `Invited ${days} days ago`;
  }
  return `Invited on ${formatShortDate(date)}`;
}

function inviteMatchesSearch(invite: Invite, search: string): boolean {
  if (!search) {
    return true;
  }
  const needle = search.toLowerCase();
  if (invite.email.toLowerCase().indexOf(needle) !== -1) {
    return true;
  }
  return !!invite.name && invite.name.toLowerCase().indexOf(needle) !== -1;
}

export default function ManageMembersModal({isOpen, toggle}: ManageMembersModalProps) {
  const user = useContext(UserContext);
  const currentUserId = toSystemUserId(user ? user.id : null);
  const currentUserEmail = user && user.email ? user.email.toLowerCase() : null;
  const companyName = user && user.brand ? user.brand.name : '';
  const companyLabel = companyName || 'your company';

  // Members list
  const [members, setMembers] = useState<Member[]>([]);
  const [pagination, setPagination] = useState<MembersPagination | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [roleCounts, setRoleCounts] = useState<Record<string, number>>({});
  const [reloadKey, setReloadKey] = useState(0);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  // Add form
  const [addMode, setAddMode] = useState<AddMode>('member');
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<RoleName>(DEFAULT_ROLE);
  const [addError, setAddError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Invite form. It opens when the email has no Vuuh account.
  const [notFoundEmail, setNotFoundEmail] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<RoleName>(DEFAULT_ROLE);
  const [inviteEmailTouched, setInviteEmailTouched] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Pending invites list
  const [invites, setInvites] = useState<Invite[]>([]);
  const [invitesTotal, setInvitesTotal] = useState(0);
  const [invitesStatus, setInvitesStatus] = useState<LoadStatus>('loading');
  const [invitesError, setInvitesError] = useState<string | null>(null);
  const [pendingInvites, setPendingInvites] = useState<Record<string, true>>({});
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  // Member rows
  const [busyMemberId, setBusyMemberId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const mountedRef = useRef(true);
  // Each open of the modal is a session. The results from an older session are ignored.
  const sessionRef = useRef(0);
  // Only the latest request of each list can update the screen.
  const requestIdRef = useRef(0);
  const rolesRequestRef = useRef(0);
  const invitesRequestRef = useRef(0);

  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const inviteNameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resetState = () => {
    setMembers([]);
    setPagination(null);
    setStatus('loading');
    setLoadError(null);
    setPage(1);
    setSearchInput('');
    setSearch('');
    setRoleCounts({});
    setHighlightId(null);
    setAddMode('member');
    setEmail('');
    setSelectedRole(DEFAULT_ROLE);
    setAddError(null);
    setIsAdding(false);
    setNotFoundEmail('');
    setInviteEmail('');
    setInviteName('');
    setInviteRole(DEFAULT_ROLE);
    setInviteEmailTouched(false);
    setIsInviting(false);
    setInviteError(null);
    setInvites([]);
    setInvitesTotal(0);
    setInvitesStatus('loading');
    setInvitesError(null);
    setPendingInvites({});
    setConfirmCancelId(null);
    setBusyMemberId(null);
    setConfirmRemoveId(null);
  };

  // Start a new session on each open and close. This effect must stay before the load effects.
  useEffect(() => {
    sessionRef.current += 1;
    requestIdRef.current += 1;
    rolesRequestRef.current += 1;
    invitesRequestRef.current += 1;
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const isSessionActive = (session: number) => mountedRef.current && session === sessionRef.current;

  const isCurrentUser = (member: Member) => {
    if (currentUserId && member.id === currentUserId) {
      return true;
    }
    return !!currentUserEmail && member.email.toLowerCase() === currentUserEmail;
  };

  const adminCount = roleCounts[ADMIN_ROLE] ? roleCounts[ADMIN_ROLE] : 0;
  const totalCount = pagination ? pagination.total_count : 0;

  const loadRoleCounts = useCallback(() => {
    const requestId = rolesRequestRef.current + 1;
    rolesRequestRef.current = requestId;
    listRoles()
      .then(items => {
        if (!mountedRef.current || requestId !== rolesRequestRef.current) {
          return;
        }
        const counts: Record<string, number> = {};
        items.forEach(item => {
          counts[item.name] = item.users_count;
        });
        setRoleCounts(counts);
      })
      .catch(() => {
        // The counts are only informative. The list stays usable without them.
      });
  }, []);

  const loadMembers = useCallback((targetPage: number, targetSearch: string) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setStatus('loading');
    setLoadError(null);
    listMembers({page: targetPage, perPage: PER_PAGE, search: targetSearch})
      .then(response => {
        if (!mountedRef.current || requestId !== requestIdRef.current) {
          return;
        }
        setMembers(response.items);
        setPagination(response.pagination);
        setStatus('success');
      })
      .catch(err => {
        if (!mountedRef.current || requestId !== requestIdRef.current) {
          return;
        }
        setLoadError(getErrorMessage(err));
        setStatus('error');
      });
  }, []);

  const loadInvites = useCallback(() => {
    const requestId = invitesRequestRef.current + 1;
    invitesRequestRef.current = requestId;
    setInvitesStatus(current => current === 'success' ? 'success' : 'loading');
    setInvitesError(null);
    fetchInvites({page: 1, perPage: INVITES_PER_PAGE})
      .then(response => {
        if (!mountedRef.current || requestId !== invitesRequestRef.current) {
          return;
        }
        setInvites(response.items);
        setInvitesTotal(response.pagination ? response.pagination.total_count : response.items.length);
        setInvitesStatus('success');
      })
      .catch(err => {
        if (!mountedRef.current || requestId !== invitesRequestRef.current) {
          return;
        }
        setInvitesError(getErrorMessage(err));
        setInvitesStatus('error');
      });
  }, []);

  // Refresh the members, the role counts and the invites together.
  const reload = () => setReloadKey(current => current + 1);

  useEffect(() => {
    if (isOpen) {
      loadRoleCounts();
    }
  }, [isOpen, reloadKey, loadRoleCounts]);

  useEffect(() => {
    if (isOpen) {
      loadMembers(page, search);
    }
  }, [isOpen, page, search, reloadKey, loadMembers]);

  useEffect(() => {
    if (isOpen) {
      loadInvites();
    }
  }, [isOpen, reloadKey, loadInvites]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const nextSearch = searchInput.trim();
      if (nextSearch !== search) {
        setPage(1);
        setSearch(nextSearch);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [searchInput, search]);

  useEffect(() => {
    if (!highlightId) {
      return;
    }
    const timeout = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    return () => clearTimeout(timeout);
  }, [highlightId]);

  // Move the focus to the first field to fill in when the form changes.
  const previousAddModeRef = useRef<AddMode>('member');
  useEffect(() => {
    if (previousAddModeRef.current === addMode) {
      return;
    }
    previousAddModeRef.current = addMode;
    if (addMode === 'invite' && inviteNameInputRef.current) {
      inviteNameInputRef.current.focus();
    } else if (addMode === 'member' && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [addMode]);

  const showFullList = () => {
    if (searchInput || search || page !== 1) {
      setSearchInput('');
      setSearch('');
      setPage(1);
    }
  };

  const setInvitePending = (inviteId: string, pending: boolean) => {
    setPendingInvites(current => {
      const next = {...current};
      if (pending) {
        next[inviteId] = true;
      } else {
        delete next[inviteId];
      }
      return next;
    });
  };

  // ----- Add a member -------------------------------------------------------

  const openInviteForm = (forEmail: string, forRole: RoleName) => {
    setNotFoundEmail(forEmail);
    setInviteEmail(forEmail);
    setInviteRole(forRole);
    setInviteName('');
    setInviteEmailTouched(false);
    setInviteError(null);
    setAddError(null);
    setAddMode('invite');
  };

  const onAdd = (e: FormEvent) => {
    e.preventDefault();
    if (isAdding) {
      return;
    }
    const trimmedEmail = email.trim();
    if (!isEmailFormat(trimmedEmail)) {
      setAddError("Enter a valid email address, for example name@company.com.");
      return;
    }
    const session = sessionRef.current;
    const submittedRole = selectedRole;
    setIsAdding(true);
    setAddError(null);
    addMember(trimmedEmail, submittedRole)
      .then(member => {
        if (!isSessionActive(session)) {
          return;
        }
        setIsAdding(false);
        setEmail('');
        setHighlightId(member.id);
        ourToast("success", `${getMemberName(member) || member.email} is added as ${submittedRole}`);
        showFullList();
        reload();
      })
      .catch(err => {
        if (!isSessionActive(session)) {
          return;
        }
        setIsAdding(false);
        if (getErrorSubstatus(err) === 'user_not_found') {
          openInviteForm(trimmedEmail, submittedRole);
          return;
        }
        setAddError(getErrorMessage(err));
      });
  };

  // ----- Invite to Vuuh -----------------------------------------------------

  const trimmedInviteEmail = inviteEmail.trim();
  const inviteEmailError = !trimmedInviteEmail
    ? "Enter the email address of the person to invite."
    : !isEmailFormat(trimmedInviteEmail) ? "Enter a valid email address, for example name@company.com." : null;
  const visibleInviteEmailError = inviteEmailTouched ? inviteEmailError : null;

  const backToAdd = () => {
    if (isInviting) {
      return;
    }
    // Keep the changes, so that the user does not type them again.
    setEmail(inviteEmail);
    setSelectedRole(inviteRole);
    setAddError(null);
    setInviteError(null);
    setAddMode('member');
  };

  const onInvite = (e: FormEvent) => {
    e.preventDefault();
    if (isInviting) {
      return;
    }
    setInviteEmailTouched(true);
    if (inviteEmailError) {
      return;
    }
    const session = sessionRef.current;
    const submittedRole = inviteRole;
    setIsInviting(true);
    setInviteError(null);
    createInvite(trimmedInviteEmail, inviteName.trim(), submittedRole)
      .then(response => {
        if (!isSessionActive(session)) {
          return;
        }
        setIsInviting(false);
        setHighlightId(response.item.id);
        ourToast("success", "Invite sent", `We emailed ${response.item.email} a link to join ${companyLabel}.`);
        setEmail('');
        setSelectedRole(DEFAULT_ROLE);
        setAddError(null);
        setNotFoundEmail('');
        setInviteEmail('');
        setInviteName('');
        setInviteRole(DEFAULT_ROLE);
        setInviteEmailTouched(false);
        setAddMode('member');
        showFullList();
        reload();
      })
      .catch(err => {
        if (!isSessionActive(session)) {
          return;
        }
        setIsInviting(false);
        setInviteError(getErrorMessage(err));
      });
  };

  const confirmCancelInvite = (invite: Invite) => {
    if (pendingInvites[invite.id]) {
      return;
    }
    const session = sessionRef.current;
    setInvitePending(invite.id, true);
    cancelInvite(invite.id)
      .then(() => {
        if (!isSessionActive(session)) {
          return;
        }
        setInvitePending(invite.id, false);
        setConfirmCancelId(null);
        setInvites(current => current.filter(item => item.id !== invite.id));
        setInvitesTotal(current => Math.max(0, current - 1));
        ourToast("success", "Invite cancelled", `The invite link sent to ${invite.email} does not work anymore.`);
        reload();
      })
      .catch(err => {
        if (!isSessionActive(session)) {
          return;
        }
        setInvitePending(invite.id, false);
        setConfirmCancelId(null);
        ourToast("error", "The invite is not cancelled", getErrorMessage(err));
        if (getErrorSubstatus(err) === 'not_found') {
          reload();
        }
      });
  };

  // ----- Member rows --------------------------------------------------------

  const onChangeRole = (member: Member, role: string) => {
    if (role === getMemberRole(member)) {
      return;
    }
    const session = sessionRef.current;
    setBusyMemberId(member.id);
    setMemberRole(member.id, role)
      .then(updated => {
        if (!isSessionActive(session)) {
          return;
        }
        setBusyMemberId(null);
        setMembers(current => current.map(m => m.id === updated.id ? updated : m));
        ourToast("success", `The role of ${getMemberName(updated) || updated.email} is changed to ${role}`);
        loadRoleCounts();
      })
      .catch(err => {
        if (!isSessionActive(session)) {
          return;
        }
        setBusyMemberId(null);
        ourToast("error", "The role is not changed", getErrorMessage(err));
      });
  };

  const onRemove = (member: Member) => {
    const session = sessionRef.current;
    setBusyMemberId(member.id);
    removeMember(member.id)
      .then(() => {
        if (!isSessionActive(session)) {
          return;
        }
        setBusyMemberId(null);
        setConfirmRemoveId(null);
        ourToast("success", `${getMemberName(member) || member.email} is removed`);
        // Go back one page if the removed member was the only one on the last page
        if (members.length === 1 && page > 1) {
          setPage(page - 1);
          loadRoleCounts();
        } else {
          reload();
        }
      })
      .catch(err => {
        if (!isSessionActive(session)) {
          return;
        }
        setBusyMemberId(null);
        ourToast("error", "The member is not removed", getErrorMessage(err));
      });
  };

  // ----- Close --------------------------------------------------------------

  const hasPendingWork = isAdding || isInviting || busyMemberId !== null || Object.keys(pendingInvites).length > 0;

  const requestClose = () => {
    if (hasPendingWork) {
      return;
    }
    toggle();
  };

  // ----- Render -------------------------------------------------------------

  const firstShown = totalCount === 0 || !pagination ? 0 : (pagination.current_page - 1) * pagination.per_page + 1;
  const lastShown = pagination ? Math.min(firstShown + members.length - 1, totalCount) : 0;
  const selectedRoleLabel = getStaticRole(selectedRole) ? selectedRole : DEFAULT_ROLE;

  const visibleInvites = invites.filter(invite => inviteMatchesSearch(invite, search));
  const showInvitesPanel = invitesStatus === 'error' || visibleInvites.length > 0;

  const renderRoleFieldset = (inputName: string,
                              value: RoleName,
                              onChange: (next: RoleName) => void,
                              disabled: boolean) => {
    const labelId = `${inputName}-label`;
    return <>
      <span className={styles.label} id={labelId}>Role</span>
      <div className={`${styles.roleList} ${disabled ? styles.roleListDisabled : ''}`}
           role="radiogroup"
           aria-labelledby={labelId}>
        {STATIC_ROLES.map(role => {
          const isSelected = role.name === value;
          const count = roleCounts[role.name] ? roleCounts[role.name] : 0;
          return <label key={role.name}
                        className={`${styles.roleCard} ${isSelected ? styles.roleCardSelected : ''}`}>
            <input type="radio"
                   name={inputName}
                   className={styles.roleRadio}
                   value={role.name}
                   checked={isSelected}
                   disabled={disabled}
                   onChange={() => onChange(role.name)}/>
            <span className={styles.roleTop}>
              <span className={styles.roleIcon}><i className={`fa-regular ${role.icon}`} aria-hidden="true"/></span>
              <strong>{role.name}</strong>
              <span className={styles.roleCount} aria-label={`${count} members with this role`}>{count}</span>
              <span className={styles.radioMark} aria-hidden="true"/>
            </span>
            <span className={styles.roleSummary}>{role.summary}</span>
            <ul className={styles.capabilities}>
              {role.capabilities.map(capability => <li key={capability}>
                <i className="fa-regular fa-check" aria-hidden="true"/>{capability}
              </li>)}
            </ul>
          </label>
        })}
      </div>
    </>;
  };

  const addPanel = <form className={styles.addPanel} onSubmit={onAdd} noValidate>
    <div className={styles.panelHeader}>
      <span className={styles.panelIcon}><i className="fa-regular fa-user-plus" aria-hidden="true"/></span>
      <div>
        <h3>Add a member</h3>
        <p>Use their email address. If they're not on Vuuh yet, you can invite them.</p>
      </div>
    </div>

    <div className={styles.addBody}>
      <label htmlFor="member-email-input" className={styles.label}>Email address</label>
      <div className={`${styles.inputWrap} ${addError ? styles.inputWrapInvalid : ''}`}>
        <i className={`fa-regular fa-envelope ${styles.inputIcon}`} aria-hidden="true"/>
        <input id="member-email-input"
               ref={emailInputRef}
               type="email"
               className={styles.input}
               placeholder="name@company.com"
               autoComplete="off"
               spellCheck={false}
               value={email}
               disabled={isAdding}
               aria-invalid={!!addError}
               aria-describedby={addError ? "member-add-error" : undefined}
               onChange={e => {
                 setEmail(e.target.value);
                 if (addError) {
                   setAddError(null);
                 }
               }}/>
      </div>
      {addError && <p id="member-add-error" className={styles.fieldError} role="alert">
        <i className="fa-regular fa-circle-exclamation" aria-hidden="true"/>{addError}
      </p>}

      {renderRoleFieldset("company-members-role", selectedRole, setSelectedRole, isAdding)}
    </div>

    <div className={styles.addFooter}>
      <button type="submit"
              className={styles.primaryButton}
              disabled={!email.trim() || isAdding}>
        <i className={isAdding ? "fa-regular fa-spinner-third fa-spin" : "fa-regular fa-user-plus"} aria-hidden="true"/>
        {isAdding ? "Adding…" : `Add as ${selectedRoleLabel}`}
      </button>
    </div>
  </form>;

  const invitePanel = <form className={`${styles.addPanel} ${styles.inviteCard}`}
                            onSubmit={onInvite}
                            noValidate
                            aria-labelledby="member-invite-title">
    <div className={styles.panelHeader}>
      <span className={styles.panelIcon}><i className="fa-regular fa-paper-plane" aria-hidden="true"/></span>
      <div>
        <h3 id="member-invite-title">Invite to Vuuh</h3>
        <p>We'll email them a link to create their account.</p>
      </div>
    </div>

    <div className={styles.addBody}>
      <div className={styles.notice} role="status">
        <span className={styles.noticeIcon} aria-hidden="true"><i className="fa-regular fa-user-magnifying-glass"/></span>
        <div>
          <strong>This person isn't on Vuuh yet</strong>
          <span>
            No account uses <span className={styles.emailChip}>{notFoundEmail}</span>. Send an invite instead and
            they'll join {companyLabel} as soon as they create their account.
          </span>
        </div>
      </div>

      <label htmlFor="member-invite-email" className={styles.label}>Email address</label>
      <div className={`${styles.inputWrap} ${visibleInviteEmailError ? styles.inputWrapInvalid : ''}`}>
        <i className={`fa-regular fa-envelope ${styles.inputIcon}`} aria-hidden="true"/>
        <input id="member-invite-email"
               type="email"
               className={styles.input}
               placeholder="name@company.com"
               autoComplete="off"
               spellCheck={false}
               value={inviteEmail}
               disabled={isInviting}
               aria-invalid={visibleInviteEmailError ? true : undefined}
               aria-describedby={visibleInviteEmailError ? "member-invite-email-error" : undefined}
               onChange={e => {
                 setInviteEmail(e.target.value);
                 setInviteError(null);
               }}
               onBlur={() => setInviteEmailTouched(true)}/>
      </div>
      {visibleInviteEmailError && <p id="member-invite-email-error" className={styles.fieldError}>
        <i className="fa-regular fa-circle-exclamation" aria-hidden="true"/>{visibleInviteEmailError}
      </p>}

      <label htmlFor="member-invite-name" className={styles.label}>
        Name <span className={styles.optionalTag}>Optional</span>
      </label>
      <div className={styles.inputWrap}>
        <i className={`fa-regular fa-id-badge ${styles.inputIcon}`} aria-hidden="true"/>
        <input id="member-invite-name"
               ref={inviteNameInputRef}
               type="text"
               className={styles.input}
               placeholder="For example, Jane Doe"
               autoComplete="off"
               maxLength={150}
               value={inviteName}
               disabled={isInviting}
               aria-describedby="member-invite-name-help"
               onChange={e => {
                 setInviteName(e.target.value);
                 setInviteError(null);
               }}/>
      </div>
      <p id="member-invite-name-help" className={styles.fieldHelp}>
        We use the name to greet them in the email and on their account.
      </p>

      {renderRoleFieldset("company-members-invite-role", inviteRole, setInviteRole, isInviting)}

      {inviteError && <div className={styles.inlineError} role="alert">
        <i className="fa-regular fa-circle-exclamation" aria-hidden="true"/>
        <div>
          <strong>The invite is not sent</strong>
          <span>{inviteError}</span>
        </div>
      </div>}
    </div>

    <div className={`${styles.addFooter} ${styles.addActionsSplit}`}>
      <button type="button"
              className={styles.secondaryButton}
              onClick={backToAdd}
              disabled={isInviting}>
        <i className="fa-regular fa-arrow-left" aria-hidden="true"/>Back
      </button>
      <button type="submit"
              className={styles.primaryButton}
              disabled={isInviting || !trimmedInviteEmail}
              title={!trimmedInviteEmail ? "Enter an email address first" : undefined}>
        <i className={isInviting ? "fa-regular fa-spinner-third fa-spin" : "fa-regular fa-paper-plane"} aria-hidden="true"/>
        <span className={styles.buttonLabel}>{isInviting ? "Sending…" : `Send invite as ${inviteRole}`}</span>
      </button>
    </div>
  </form>;

  const membersPanel = <section className={styles.listPanel} aria-labelledby="members-list-title">
    <div className={styles.listHeader}>
      <div>
        <h3 id="members-list-title">Members</h3>
        <p>{`Admins: ${adminCount}. The company must keep at least one admin.`}</p>
      </div>
      <div className={styles.searchWrap}>
        <i className={`fa-regular fa-magnifying-glass ${styles.inputIcon}`} aria-hidden="true"/>
        <input type="search"
               className={styles.input}
               placeholder="Search by name or email"
               aria-label="Search members and invites"
               value={searchInput}
               onChange={e => setSearchInput(e.target.value)}/>
      </div>
    </div>

    <div className={styles.listMeta}>
      {status === 'success' && totalCount > 0 && <span>
        Showing <strong>{firstShown}–{lastShown}</strong> of <strong>{totalCount}</strong> {totalCount === 1 ? 'member' : 'members'}
      </span>}
      {status === 'loading' && <span><i className="fa-regular fa-spinner-third fa-spin" aria-hidden="true"/> Loading members…</span>}
    </div>

    <div className={styles.memberList}>
      {status === 'error' && <div className={styles.emptyState}>
        <span className={`${styles.emptyIcon} ${styles.emptyIconError}`}><i className="fa-regular fa-circle-exclamation" aria-hidden="true"/></span>
        <strong>The members cannot be loaded</strong>
        <p>{loadError}</p>
        <button type="button" className={styles.secondaryButton} onClick={reload}>
          <i className="fa-regular fa-arrows-rotate" aria-hidden="true"/>Try again
        </button>
      </div>}

      {status !== 'error' && members.length === 0 && status === 'success' && <div className={styles.emptyState}>
        <span className={styles.emptyIcon}><i className="fa-regular fa-user-magnifying-glass" aria-hidden="true"/></span>
        <strong>{search ? 'No members match your search' : 'No members yet'}</strong>
        <p>{search ? 'Use a different name or email address.' : 'Add the first member with the form on the left.'}</p>
      </div>}

      {status !== 'error' && members.map(member => {
        const role = getMemberRole(member);
        const staticRole = getStaticRole(role);
        const isSelf = isCurrentUser(member);
        const isBusy = busyMemberId === member.id;
        const isLastAdmin = role === ADMIN_ROLE && adminCount <= 1;
        const isLocked = isSelf || isLastAdmin;
        const lockReason = isSelf
          ? "You cannot change your own access."
          : "The company must keep at least one admin.";
        const name = getMemberName(member);
        const highlightClass = highlightId === member.id ? styles.memberRowHighlight : '';

        if (confirmRemoveId === member.id) {
          return <div key={member.id} className={`${styles.memberRow} ${styles.memberRowConfirm}`}>
            <span className={`${styles.avatar} ${styles.avatarDanger}`}><i className="fa-regular fa-user-minus" aria-hidden="true"/></span>
            <div className={styles.memberInfo}>
              <strong>{`Remove ${name || member.email}?`}</strong>
              <span>The person loses access to {companyName || 'this company'}. The Vuuh account stays.</span>
            </div>
            <div className={styles.memberActions}>
              <button type="button" className={styles.secondaryButton} onClick={() => setConfirmRemoveId(null)} disabled={isBusy} autoFocus>
                Cancel
              </button>
              <button type="button" className={styles.dangerButton} onClick={() => onRemove(member)} disabled={isBusy}>
                {isBusy && <i className="fa-regular fa-spinner-third fa-spin" aria-hidden="true"/>}
                Remove
              </button>
            </div>
          </div>;
        }

        return <div key={member.id}
                    className={`${styles.memberRow} ${!member.is_active ? styles.memberRowInactive : ''} ${highlightClass}`}>
          <span className={styles.avatar} aria-hidden="true">{getInitials(member)}</span>
          <div className={styles.memberInfo}>
            <strong>
              <span className={styles.memberName}>{name || member.email}</span>
              {isSelf && <span className={styles.youBadge}>You</span>}
              {!member.is_active && <span className={styles.inactiveBadge}>Inactive</span>}
            </strong>
            <span>
              {name && <>{member.email}<span className={styles.dot} aria-hidden="true">·</span></>}
              {formatLastLogin(member.last_login)}
            </span>
          </div>
          <div className={styles.memberActions} title={isLocked ? lockReason : undefined}>
            <div className={`${styles.roleSelectWrap} ${isLocked || isBusy ? styles.roleSelectDisabled : ''}`}>
              <i className={`fa-regular ${staticRole ? staticRole.icon : 'fa-user'} ${styles.roleSelectIcon}`} aria-hidden="true"/>
              <select className={styles.roleSelect}
                      value={role}
                      disabled={isLocked || isBusy}
                      aria-label={`Role of ${name || member.email}`}
                      onChange={e => onChangeRole(member, e.target.value)}>
                {!staticRole && <option value={role}>{role}</option>}
                {STATIC_ROLES.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
              </select>
              {isBusy
                ? <i className={`fa-regular fa-spinner-third fa-spin ${styles.roleSelectChevron}`} aria-hidden="true"/>
                : <i className={`fa-regular fa-chevron-down ${styles.roleSelectChevron}`} aria-hidden="true"/>}
            </div>
            <button type="button"
                    className={styles.removeButton}
                    disabled={isLocked || isBusy}
                    aria-label={`Remove ${name || member.email}`}
                    title={isLocked ? lockReason : "Remove member"}
                    onClick={() => {
                      setConfirmCancelId(null);
                      setConfirmRemoveId(member.id);
                    }}>
              <i className="fa-regular fa-user-minus" aria-hidden="true"/>
            </button>
          </div>
        </div>;
      })}
    </div>

    {pagination && pagination.total_pages > 1 && <div className={styles.pagination}>
      <button type="button"
              className={styles.secondaryButton}
              disabled={page <= 1 || status === 'loading'}
              onClick={() => setPage(page - 1)}>
        <i className="fa-regular fa-chevron-left" aria-hidden="true"/>Previous
      </button>
      <span>Page {pagination.current_page} of {pagination.total_pages}</span>
      <button type="button"
              className={styles.secondaryButton}
              disabled={page >= pagination.total_pages || status === 'loading'}
              onClick={() => setPage(page + 1)}>
        Next<i className="fa-regular fa-chevron-right" aria-hidden="true"/>
      </button>
    </div>}
  </section>;

  const renderInviteRow = (invite: Invite) => {
    const staticRole = getStaticRole(invite.role);
    const isPending = !!pendingInvites[invite.id];
    const label = invite.name ? invite.name : invite.email;

    if (confirmCancelId === invite.id) {
      return <div key={invite.id} className={`${styles.memberRow} ${styles.memberRowConfirm}`}>
        <span className={`${styles.avatar} ${styles.avatarDanger}`}><i className="fa-regular fa-xmark" aria-hidden="true"/></span>
        <div className={styles.memberInfo}>
          <strong>Cancel invite?</strong>
          <span>The link sent to {invite.email} stops working.</span>
        </div>
        <div className={styles.memberActions}>
          <button type="button"
                  className={styles.secondaryButton}
                  onClick={() => setConfirmCancelId(null)}
                  disabled={isPending}
                  autoFocus>
            Keep
          </button>
          <button type="button"
                  className={styles.dangerButton}
                  onClick={() => confirmCancelInvite(invite)}
                  disabled={isPending}>
            {isPending && <i className="fa-regular fa-spinner-third fa-spin" aria-hidden="true"/>}
            {isPending ? "Cancelling…" : "Cancel invite"}
          </button>
        </div>
      </div>;
    }

    return <div key={invite.id}
                className={`${styles.memberRow} ${highlightId === invite.id ? styles.memberRowHighlight : ''}`}>
      <span className={`${styles.avatar} ${styles.avatarInvite}`} aria-hidden="true">
        <i className="fa-regular fa-envelope"/>
      </span>
      <div className={styles.memberInfo}>
        <strong>
          <span className={styles.memberName} title={label}>{label}</span>
          <span className={styles.pillWarning}>Invited</span>
        </strong>
        <span>
          {invite.name && <>{invite.email}<span className={styles.dot} aria-hidden="true">·</span></>}
          {formatInvitedAt(invite.created_at)}
        </span>
      </div>
      <div className={styles.memberActions}>
        <span className={styles.rolePill} title={`Joins as ${invite.role}`}>
          <i className={`fa-regular ${staticRole ? staticRole.icon : 'fa-user'}`} aria-hidden="true"/>
          {invite.role}
        </span>
        <button type="button"
                className={styles.removeButton}
                disabled={isPending}
                aria-label={`Cancel the invite for ${invite.email}`}
                title="Cancel invite"
                onClick={() => {
                  setConfirmRemoveId(null);
                  setConfirmCancelId(invite.id);
                }}>
          <i className="fa-regular fa-xmark" aria-hidden="true"/>
        </button>
      </div>
    </div>;
  };

  const invitesPanel = showInvitesPanel && <section className={`${styles.listPanel} ${styles.invitesPanel}`}
                                                    aria-labelledby="invites-list-title">
    <div className={styles.listHeader}>
      <div>
        <h3 id="invites-list-title">
          Pending invites
          {invitesStatus === 'success' && <span className={styles.titleCount}>
            {search ? visibleInvites.length : invitesTotal}
          </span>}
        </h3>
        <p>They get access when they open the link in the email and create their Vuuh account.</p>
      </div>
    </div>

    <div className={styles.invitesScroll}>
      {invitesStatus === 'error'
        ? <div className={styles.listError} role="alert">
          <i className="fa-regular fa-circle-exclamation" aria-hidden="true"/>
          <div>
            <strong>The pending invites cannot be loaded</strong>
            <span>{invitesError}</span>
          </div>
          <button type="button" className={styles.linkButton} onClick={reload}>Try again</button>
        </div>
        : visibleInvites.map(renderInviteRow)}
    </div>

    {invitesStatus === 'success' && invitesTotal > invites.length && <p className={styles.listFootnote}>
      Showing the {invites.length} most recent of {invitesTotal} pending invites.
    </p>}
  </section>;

  return <Modal isOpen={isOpen}
                toggle={requestClose}
                className={styles.modal}
                contentClassName={styles.content}>
    <header className={styles.header}>
      <span className={styles.headerIcon}><i className="fa-regular fa-users" aria-hidden="true"/></span>
      <div className={styles.headerText}>
        {companyName && <span className={styles.eyebrow}>{companyName}</span>}
        <h2 className={styles.title}>Company members</h2>
        <p className={styles.subtitle}>Add or invite people by email, choose what they can do, and remove access when they leave.</p>
      </div>
      <div className={styles.headerActions}>
        {pagination && <span className={styles.countChip}>
          <strong>{totalCount}</strong>{totalCount === 1 ? 'member' : 'members'}
        </span>}
        {invitesTotal > 0 && <span className={`${styles.countChip} ${styles.countPillWarning}`}>
          <strong>{invitesTotal}</strong>pending
        </span>}
        <button type="button"
                className={styles.iconButton}
                onClick={requestClose}
                disabled={hasPendingWork}
                title={hasPendingWork ? "Wait until your changes are saved" : "Close"}
                aria-label="Close">
          <i className="fa-regular fa-xmark" aria-hidden="true"/>
        </button>
      </div>
    </header>

    <div className={styles.layout}>
      {addMode === 'invite' ? invitePanel : addPanel}
      <div className={styles.listColumn}>
        {membersPanel}
        {invitesPanel}
      </div>
    </div>
  </Modal>
}
